// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useMemo, useState } from 'react';
import type { VoiceProfile } from '../../api/intelligenceAdminApi';
import { useSpeechSynthesisVoices } from '../../hooks/useSpeechSynthesisVoices';

interface Props {
  enabled: boolean;
  profile: VoiceProfile | undefined;
  agentName?: string;
  onChange: (next: { enabled: boolean; profile: VoiceProfile }) => void;
}

const COMMON_LOCALES = [
  'en-US', 'en-GB', 'en-IE', 'en-AU', 'en-CA', 'fr-FR', 'es-ES', 'de-DE',
  'it-IT', 'pt-PT', 'pt-BR', 'nl-NL',
];

const VoiceProfileEditor: React.FC<Props> = ({ enabled, profile, agentName, onChange }) => {
  const browserVoices = useSpeechSynthesisVoices();
  const current: VoiceProfile = useMemo(() => ({
    locale: 'en-US', gender: 'female', rate: 1.0, pitch: 1.0,
    preferredVoices: [], greeting: '',
    ...profile,
  }), [profile]);
  const [voiceInput, setVoiceInput] = useState('');

  const update = (patch: Partial<VoiceProfile>) =>
    onChange({ enabled, profile: { ...current, ...patch } });

  const addPreferredVoice = () => {
    const trimmed = voiceInput.trim();
    if (!trimmed) return;
    const next = [...(current.preferredVoices ?? []), trimmed].slice(0, 10);
    update({ preferredVoices: next });
    setVoiceInput('');
  };

  const removePreferred = (idx: number) => {
    const next = (current.preferredVoices ?? []).filter((_, i) => i !== idx);
    update({ preferredVoices: next });
  };

  const testVoice = () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(
      current.greeting || `Hello, I am ${agentName ?? 'Fin'}.`,
    );
    u.lang = current.locale ?? 'en-US';
    u.rate = current.rate ?? 1.0;
    u.pitch = current.pitch ?? 1.0;
    const voice = browserVoices.find(v => current.preferredVoices?.includes(v.name))
      ?? browserVoices.find(v => v.lang === current.locale)
      ?? browserVoices[0];
    if (voice) u.voice = voice;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  return (
    <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 16, marginTop: 16 }}>
      <legend style={{ fontWeight: 600, padding: '0 8px' }}>Voice chat</legend>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange({ enabled: e.target.checked, profile: current })}
          data-testid="voice-enabled"
        />
        Enable voice chat for this scope
      </label>

      <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', padding: 8, borderRadius: 4, fontSize: 13, marginBottom: 12 }}>
        Voice chat uses the user's browser. Audio recognition typically routes
        through the browser vendor's cloud (Google for Chrome, Microsoft for Edge).
        Confirm this complies with your data-residency obligations.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label>
          Locale
          <select
            value={current.locale ?? 'en-US'}
            onChange={(e) => update({ locale: e.target.value })}
            disabled={!enabled}
            style={inputStyle}
            data-testid="voice-locale"
          >
            {COMMON_LOCALES.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>

        <label>
          Gender
          <select
            value={current.gender ?? 'female'}
            onChange={(e) => update({ gender: e.target.value as VoiceProfile['gender'] })}
            disabled={!enabled}
            style={inputStyle}
            data-testid="voice-gender"
          >
            <option value="female">female</option>
            <option value="male">male</option>
            <option value="any">any</option>
          </select>
        </label>

        <label>
          Rate ({(current.rate ?? 1).toFixed(2)})
          <input
            type="range" min={0.5} max={2.0} step={0.05}
            value={current.rate ?? 1.0}
            onChange={(e) => update({ rate: Number(e.target.value) })}
            disabled={!enabled}
            data-testid="voice-rate"
          />
        </label>

        <label>
          Pitch ({(current.pitch ?? 1).toFixed(2)})
          <input
            type="range" min={0.5} max={2.0} step={0.05}
            value={current.pitch ?? 1.0}
            onChange={(e) => update({ pitch: Number(e.target.value) })}
            disabled={!enabled}
            data-testid="voice-pitch"
          />
        </label>
      </div>

      <label style={{ display: 'block', marginTop: 12 }}>
        Greeting
        <input
          type="text"
          value={current.greeting ?? ''}
          onChange={(e) => update({ greeting: e.target.value })}
          disabled={!enabled}
          maxLength={400}
          placeholder={`Hi, I'm ${agentName ?? 'Fin'}. I'll help you complete your fact-find.`}
          style={inputStyle}
          data-testid="voice-greeting"
        />
      </label>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Preferred voices (in order)</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
          Voice names differ per browser. Add 2–4 names ranked by your priority.
          The first available match in the user's browser is used. Available in
          this browser: {browserVoices.length}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input
            type="text"
            value={voiceInput}
            onChange={(e) => setVoiceInput(e.target.value)}
            disabled={!enabled}
            placeholder="e.g. Samantha"
            style={{ ...inputStyle, flex: 1 }}
            data-testid="voice-add-input"
          />
          <button type="button" onClick={addPreferredVoice} disabled={!enabled} data-testid="voice-add">
            Add
          </button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {(current.preferredVoices ?? []).map((name, idx) => (
            <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: '#f9fafb', marginBottom: 4, borderRadius: 4 }}>
              <span>{idx + 1}. {name}</span>
              <button type="button" onClick={() => removePreferred(idx)} disabled={!enabled}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={testVoice} disabled={!enabled} data-testid="voice-test">
          Test voice (in this browser)
        </button>
      </div>
    </fieldset>
  );
};

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: 4,
  border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px',
};

export default VoiceProfileEditor;
